import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Search, FileVideo } from 'lucide-react'
import { Advertisement } from '@magnetar/admin-api-interfaces'
import { useAdSuppliers } from '@/hooks/useAdSuppliers'

interface AddAdvertisementDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  advertisements: Advertisement[]
  /**
   * 廣告 → 已在當前梯次中出現次數的對照表（顯示提示用）。
   * 同一廣告可重複加入，這裡只是讓使用者看到當前次數，不會影響可選性。
   */
  existingAdCounts?: Record<number, number>
  onAdd: (selectedAds: Advertisement[]) => void
}

export default function AddAdvertisementDialog({
  open,
  onOpenChange,
  advertisements,
  existingAdCounts = {},
  onAdd,
}: AddAdvertisementDialogProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('all')

  const { adSuppliers, isLoading: isSuppliersLoading } = useAdSuppliers({
    page: 1,
    pageSize: 1000,
  })

  const filteredAds = advertisements.filter((ad) => {
    const matchesSearch =
      ad.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ad.description?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesSupplier =
      selectedSupplierId === 'all' || ad.supplier_id === Number(selectedSupplierId)

    return matchesSearch && matchesSupplier
  })

  const handleToggle = (adId: number) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(adId)) {
      newSelected.delete(adId)
    } else {
      newSelected.add(adId)
    }
    setSelectedIds(newSelected)
  }

  const handleAdd = () => {
    const selectedAds = advertisements.filter((ad) => selectedIds.has(ad.advertisement_id))
    onAdd(selectedAds)
    setSelectedIds(new Set())
    setSearchTerm('')
    setSelectedSupplierId('all')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>選擇廣告</DialogTitle>
          <DialogDescription>選擇要加入到梯次的廣告</DialogDescription>
        </DialogHeader>

        <div className="px-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜尋廣告標題或描述..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedSupplierId} onValueChange={setSelectedSupplierId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="選擇供應商" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部</SelectItem>
                {isSuppliersLoading ? (
                  <SelectItem value="loading" disabled>
                    載入中...
                  </SelectItem>
                ) : (
                  adSuppliers?.map((supplier) => (
                    <SelectItem
                      key={supplier.supplier_id}
                      value={supplier.supplier_id.toString()}
                    >
                      {supplier.supplier_name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {filteredAds.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">沒有找到符合的廣告</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAds.map((ad) => (
                <div
                  key={ad.advertisement_id}
                  className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                  onClick={() => handleToggle(ad.advertisement_id)}
                >
                  <Checkbox
                    checked={selectedIds.has(ad.advertisement_id)}
                    onCheckedChange={() => handleToggle(ad.advertisement_id)}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-0.5 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{ad.title || '無標題廣告'}</p>
                    {ad.description && (
                      <p className="text-sm text-muted-foreground truncate">{ad.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="secondary" className="text-xs shrink-0">
                        <FileVideo className="mr-1 h-3 w-3" />
                        {ad.media?.length || 0} 個媒體
                      </Badge>
                      {ad.is_active ? (
                        <Badge className="bg-green-500 text-xs shrink-0">啟用</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          停用
                        </Badge>
                      )}
                      {existingAdCounts[ad.advertisement_id] ? (
                        <Badge variant="outline" className="text-xs shrink-0">
                          已加入 {existingAdCounts[ad.advertisement_id]} 次
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 pb-6">
          <div className="flex items-center justify-between w-full">
            <p className="text-sm text-muted-foreground">
              篩選出 {filteredAds.length} 個 / 總共 {advertisements.length} 個 / 已選擇{' '}
              {selectedIds.size} 個
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button onClick={handleAdd} disabled={selectedIds.size === 0}>
                加入 {selectedIds.size > 0 && `(${selectedIds.size})`}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
